import { useEffect, useState } from "react";
import { usePatientAuth } from "@/contexts/PatientAuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const RELATIONS = ["Self", "Mother", "Father", "Spouse", "Child", "Grandparent", "Sibling", "Other"];

export default function FamilyManager({ onSelect }) {
  const { api } = usePatientAuth();
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", age: "", relation: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/patient/me/family");
    setMembers(data);
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/patient/me/family", { ...form, age: parseInt(form.age) });
      setForm({ name: "", age: "", relation: "" });
      load();
      toast.success("Family member added");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/patient/me/family/${id}`);
    load();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="family-open-btn">
          <Users className="w-4 h-4" /> Family
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Family Members</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="family-list">
          {members.length === 0 ? <p className="text-sm text-slate-500">No members yet. Add one below to book on their behalf.</p> :
            members.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`family-${m.id}`}>
                <div>
                  <div className="font-medium text-slate-900 text-sm">{m.name} <span className="text-xs text-slate-500">· {m.age}y · {m.relation}</span></div>
                </div>
                <div className="flex gap-1">
                  {onSelect && <Button size="sm" variant="ghost" onClick={() => { onSelect(m); setOpen(false); }} data-testid={`family-pick-${m.id}`}>Pick</Button>}
                  <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`family-del-${m.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
        </div>
        <form onSubmit={add} className="space-y-3 border-t pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="family-name" /></div>
            <div><Label>Age</Label><Input type="number" min="0" max="120" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} required data-testid="family-age" /></div>
          </div>
          <div>
            <Label>Relation</Label>
            <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
              <SelectTrigger data-testid="family-relation"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !form.relation} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="family-add-btn">
              <Plus className="w-4 h-4" /> Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
